import React, { Fragment, useMemo, useState } from 'react'
import Actions from '../../Actions'
import ga4ReactClickHandler from 'content/src/assets/common/scripts/ga4_support/react/ga4ReactClickHandler'
import { IWeatherDisplayTemplateProps } from '../../../types'
import iconMap from '../../../settings/iconMap'
import { langMap } from '../../../settings/config'
import i18n from '../../../../../../utilities/translation'

const pageLanguage = document.documentElement.getAttribute('lang')
const mappedPageLanguage = langMap(pageLanguage)

const WeatherDisplayMoment: React.FC<IWeatherDisplayTemplateProps> = ({
  celsius,
  ariaLabel,
  ariaLiveVal,
  temperatureData,
  dailyForecast,
  locationData,
  iconData,
  labelData,
  texts,
  setAriaLabel,
  toggleTemp,
  fixTemp,
  getActiveUnit
}) => {
  const [isDailyForecastVisible, setDailyForecastVisibility] = useState(false)
  const { temperatureFeelsLikeFixed, temperatureMaxFixed, temperatureMinFixed, temperatureFixed } =
    temperatureData

  const showHideDailyForecast = () => {
    setDailyForecastVisibility(previousState => !previousState)
  }

  const generateDailyItems = items => (
    <Fragment>
      {items.map(item => {
        return (
          <div className="daily-item" key={item.day}>
            <div className="daily-item__title">{item.day}</div>
            <div className="daily-item__icon-container">
              <img
                className="daily-item__icon"
                tabIndex={0}
                src={item.iconUrl}
                alt={item.description}
              />
            </div>
            <div className="daily-item__description">{item.description}</div>
            <div className="daily-item__temperature">{item.fixedTemperature}°</div>
          </div>
        )
      })}
    </Fragment>
  )

  const degrees = useMemo(
    () => (
      <div className="tContainer" tabIndex={-1}>
        <span className="tValue">{temperatureFixed}</span>
        <span className={`tLabel ${celsius ? '' : 'active'}`}>°F</span>
        <span className="tPipe">|</span>
        <span className={`tLabel ${celsius ? 'active' : ''}`}>°C</span>
      </div>
    ),
    [celsius]
  )

  const dailyItemsWithDays = useMemo(
    () => {
      // NOTE: This is not 100% accurate as the WeatherBug API returns a UTC date string we aren't using, but because
      // we are planning to update all of this in the near future it was decided to use this hacky way instead for now
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const startDate = (new Date()).getDay()
      return dailyForecast.slice(0, 5).map( (item, index) => {
        item.day = days[(startDate + index)%7]
        item.iconUrl = iconMap[item.iconCode].iconUrl
        item.description = iconMap[item.iconCode].description[mappedPageLanguage]
        return item
      })
    },
    [dailyForecast]
  )
  const dailyItemsFullData = useMemo(
    () =>
      dailyItemsWithDays.slice(0, 5).map(item => {
        item.fixedTemperature = fixTemp(item.temperature, celsius)

        return item
      }),
    [dailyForecast, celsius]
  )

  const dailyItemsTemplate = useMemo(
    () => generateDailyItems(dailyItemsFullData),
    [dailyForecast, celsius]
  )

  const iconDescription = useMemo(
    () => iconData.iconLabel[mappedPageLanguage] ?? '',
    [mappedPageLanguage, iconData]
  )

  return (
    <div
      className="block-main__container"
      data-mp-content-type="weather_widget"
      data-mp-share-title={`${temperatureFixed} ${getActiveUnit(celsius)}`}
      data-mp-share-description={`${locationData.cityName}, ${locationData.stateName}`}
    >
      <div className="block-main__body body">
        <div className="body__left-section">
          <div className="body__place-container" tabIndex={0}>
            <p className="body__place">{`${locationData.cityName}, ${locationData.stateName}`}</p>
          </div>

          <div className="body__icon-container">
            <img
              className="body__icon body__icon--big"
              tabIndex={0}
              src={iconData.iconPath}
              alt={iconDescription}
            />
          </div>

          <div className="body__description-container">
            <p className="body__description">{iconDescription}</p>
          </div>
        </div>
        <div className="body__right-section">
          <div className="body__weather-link-container">
            <a
              data-mp-cct
              className="body__weather-link"
              href={'https://accuweather.com?sourceProviderId=dt'}
              onClick={ga4ReactClickHandler}
            >
              accuweather.com&reg;
            </a>
          </div>

          <div className="body__temperature-container">
            <div className="body__temperature-main">
              <button
                className="mainTemp"
                aria-label={ariaLabel}
                aria-live={ariaLiveVal}
                onFocus={() => setAriaLabel(labelData.degreesLabel)}
                onClick={() => toggleTemp(temperatureFixed, texts.click_to_switch)}
              >
                {degrees}
              </button>
            </div>

            <div className="body__temperature-high-low">
              {temperatureMaxFixed && (
                <div className="highTemp" tabIndex={0} aria-label={labelData.highTempLabel}>
                  {temperatureMaxFixed}&deg; <span className="arrow">{'\u00A0'}</span>
                </div>
              )}
              {temperatureMinFixed && (
                <div className="lowTemp" tabIndex={0} aria-label={labelData.lowTempLabel}>
                  {temperatureMinFixed}&deg; <span className="arrow">{'\u00A0'}</span>
                </div>
              )}
            </div>

            <div className="body__temperature-feels-like">
              {temperatureFeelsLikeFixed && (
                <p className="feelsTemp" tabIndex={0} aria-label={labelData.feelsLikeLabel}>
                  {texts.feels} {temperatureFeelsLikeFixed}&deg;
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="block-main__footer footer">
        <button
          className={`footer__show-more ${isDailyForecastVisible ? 'arrow-up' : 'arrow-down'}`}
          onClick={showHideDailyForecast}
        >
          {i18n('5-Day Forecast', mappedPageLanguage)}
        </button>
        <Actions personalize />
      </div>
      {isDailyForecastVisible && (
        <div className="block-main__daily daily">{dailyItemsTemplate}</div>
      )}
    </div>
  )
}

export default WeatherDisplayMoment
